import React, { useCallback, useEffect, useMemo, useState } from "react";
import useTablePagination from "@/hooks/useTablePagination.ts";
import useDebouncedSearch from "@/hooks/useDebouncedSearch.ts";
import {
  Box,
  Modal as MuiModal,
  TableBody,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  StyledPinnedCell,
  StyledTable,
  StyledTableCard,
  StyledTableCell,
  StyledTableCellTruncated,
  StyledTableHeader,
  StyledTableRow,
  StyledTooltip,
} from "@/components/styled";
import TablePagination from "@/components/TablePagination";
import { getEnrollmentBadgeVariant } from "@/utils/getEnrollmentBadgeVariant.ts";
import useTableFilter from "@/hooks/useTableFilter.ts";
import { createSearchParams, useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { useProjectsStore } from "@/store/projectsStore.ts";
import { FileRecord, ProjectRecord } from "@/types";
import DangerModal from "@/components/DangerModal";
import { archiveProjectReq, restoreProjectReq } from "@/api/projectsApi";
import { updateEnrolmentStatusUtil } from "@/utils/projectStatusUtils";
import BadgeSelect from "@/pages/admin-pages/UserManagementPage/components/BadgeSelect.tsx";
import {
  CONTRACTOR_ENROLLMENT_STATUS_ITEMS,
  INCENTIVE_STATUS,
  INCENTIVE_STATUS_ITEMS,
  LAST_UPDATED_SORT_ITEMS,
  PROJECTS_FILTER_ITEMS,
  SIZING_FILTER_ITEMS,
} from "@/constants";
import { useAuthStore } from "@/store/authStore.ts";
import { formatDate, formatProjectDate } from "@/utils/formatDate.ts";
import { removeLeadingZero } from "@/utils/removeLeadingZero.ts";
import IncentiveStatusSelect from "@/pages/ProjectsPage/components/IncentiveStatusSelect.tsx";
import RejectedProjectModal from "@/pages/ProjectsPage/components/RejectedProjectModal.tsx";
import GeneralCheckbox from "@/pages/ProjectsPage/components/GeneralCheckbox.tsx";
import RowCheckbox from "@/pages/ProjectsPage/components/RowCheckbox.tsx";
import ProjectsSelectedInfo from "@/pages/ProjectsPage/components/ProjectsSelectedInfo.tsx";
import useFetchDataWithLoading from "@/hooks/useFetchDataWithLoading.ts";
import CorrectionNeededModal from "@/pages/ProjectsPage/components/CorrectionNeededModal.tsx";
import TableFilter from "@/components/TableFilter";
import CommonTableActions from "@/components/CommonTableActions";
import AddProjectModal from "@/pages/ProjectDetailsPage/components/AddProjectModal.tsx";
import CollapsableRow, { CellWithHeader } from "@/components/CollapsableRow";
import ProjectRowActions from "@/pages/ProjectsPage/components/ProjectRowActions.tsx";
import PtoLetterCheckbox from "@/pages/ProjectsPage/components/PTOLetterCheckbox.tsx";
import BatteryOperationalizedSelect from "@/pages/ProjectsPage/components/BatteryOperationalizedSelect.tsx";
import { calculateUsableBatteryCapacity } from "@/utils/calculateUsableBatteryCapacity.ts";
import { removeTrailZero } from "@/utils/numberFormatting";
import {
  getEnrollmentStatusChangeAllowed,
  getEnrollmentStatusItems,
} from "@/utils/contractorEnrollmentStatusHelpers.ts";
import { useApplicationStore } from "@/store/applicationStore.ts";
import { getStatusTooltipContent } from "@/utils/getStatusTooltipContent.ts";
import TableSort from "@/components/TableSort";
import useTableSort from "@/hooks/useTableSort.ts";
import SendPerformanceIncentiveEmailButton from "@/components/SendPerformanceIncentiveEmailButton";
import { useSelectedRowsStore } from "@/store/selectedRowsStore.ts";
import { getSizingPassedFilter as getSizingPassedFilterValue } from "@/utils/getSizingPassedFilter";

function createData(
  project: Partial<ProjectRecord>,
  id: number,
  customerName: string,
  address: string,
  incentiveStatus: INCENTIVE_STATUS | undefined,
  enrolmentStatus: ProjectRecord["enrolmentStatus"],
  contractor: string | null,
  batteryOem: string | null,
  batteryModel: string,
  batteryType: string,
  usableCapacity: string,
  upfrontIncentivePayment: string,
  participantEnrollment: FileRecord,
  systemSizingTool: FileRecord,
  contract: FileRecord,
  createdAt: string,
  updatedAt: string,
  latestEnrolmentUpdate: string | null,
  latestIncentiveUpdate: string | null,
  oemId: number | null,
  approvalDate: string | null
) {
  return {
    ...(project || {}),
    id,
    customerName,
    address,
    incentiveStatus,
    enrolmentStatus,
    contractor,
    batteryOem,
    batteryModel,
    batteryType,
    usableCapacity,
    upfrontIncentivePayment,
    participantEnrollment,
    systemSizingTool,
    contract,
    createdAt: formatProjectDate(createdAt),
    updatedAt: formatProjectDate(updatedAt),
    latestEnrolmentUpdate: latestEnrolmentUpdate
      ? formatProjectDate(latestEnrolmentUpdate)
      : null,
    latestIncentiveUpdate: latestIncentiveUpdate
      ? formatProjectDate(latestIncentiveUpdate)
      : null,
    oemId,
    approvalDate: approvalDate ? formatProjectDate(approvalDate) : null,
  };
}

const PROJECTS_TABLE_ID = "PROJECTS_TABLE_ID";

const ProjectsTable = () => {
  const isApplicationsActive = useApplicationStore((state) => state.isActive);
  const navigate = useNavigate();
  const [yScroll, setYScroll] = useState(0);
  const [deleteProjectId, setDeleteProjectId] = useState(0);
  const [rejectProjectId, setRejectProjectId] = useState(0);
  const [correctionNeededProjectId, setCorrectionNeededProjectId] = useState(0);
  const [restoreProjectId, setRestoreProjectId] = useState(0);
  const [isPTOLetterFilter, setPTOLetterFilter] = useState(false);
  const [batteryOperationalizedFilter, setBatteryOperationalizedFilter] = useState<string | undefined>(undefined);
  const [sizingFilter, setSizingFilter] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchDataWithLoading = useFetchDataWithLoading();

  const role = useAuthStore((state) => state.role);

  const { allProjects, totalProjectsCount } = useProjectsStore(
    (state) => state.allProjects
  );

  const selectedRows = useSelectedRowsStore(
    (state) => state.tables[PROJECTS_TABLE_ID]?.selectedRows
  );

  const getAllProjects = useProjectsStore((state) => state.getAllProjects);
  const rows = useMemo(() => {
    return allProjects?.map(
      ({
        id,
        customerName,
        address,
        incentiveStatus,
        enrolmentStatus,
        contractor,
        batteryOem,
        batteryModel,
        batteryType,
        usableCapacity,
        upfrontIncentivePayment,
        participantEnrollment,
        systemSizingTool,
        contract,
        createdAt,
        updatedAt,
        latestIncentiveUpdate,
        latestEnrolmentUpdate,
        oemId,
        approvalDate,
        ...project
      }) =>
        createData(
          project,
          id,
          customerName,
          address,
          incentiveStatus,
          enrolmentStatus,
          contractor,
          batteryOem,
          batteryModel,
          batteryType,
          usableCapacity,
          upfrontIncentivePayment,
          participantEnrollment,
          systemSizingTool,
          contract,
          createdAt,
          updatedAt,
          latestEnrolmentUpdate,
          latestIncentiveUpdate,
          oemId,
          approvalDate
        )
    );
  }, [allProjects]);

  const currentRowIds = useMemo(() => rows?.map((row) => row.id) || [], [rows]);

  const { page, rowsPerPage, handleChangeRowsPerPage, handleChangePage } =
    useTablePagination(rows, totalProjectsCount);
  const {
    searchValue,
    handleDebounceSearchValue: originalHandleDebounceSearchValue,
  } = useDebouncedSearch(handleChangePage);

  const handleDebounceSearchValue = (value: string) => {
    const sanitizedValue = removeLeadingZero(value);
    originalHandleDebounceSearchValue(sanitizedValue);
  };

  const { sort, handleSortChange } = useTableSort();

  const {
    filters: enrollmentFilters,
    handleFilterChange: handleFilterChangeEnrollment,
  } = useTableFilter(handleChangePage);
  const {
    filters: incentiveFilters,
    handleFilterChange: handleFilterChangeIncentive,
  } = useTableFilter(handleChangePage);
  const {
    filters: sizingFilters,
    handleFilterChange: handleFilterChangeSizing,
  } = useTableFilter(handleChangePage);

  // Sizing is sent separately as `sizing_passed` and must stay out of the generic `filter` list:
  // the backend matches that list against enrolment and incentive statuses, and "pending" is also
  // an enrolment status, so including it silently filtered to enrolmentStatus = 'pending' as well.
  const filters = useMemo(() => {
    return [...enrollmentFilters, ...incentiveFilters];
  }, [enrollmentFilters, incentiveFilters]);

  const getSizingPassedFilter = useMemo(
    () => getSizingPassedFilterValue(sizingFilters),
    [sizingFilters]
  );

  const getProjectsReq = useCallback(() => {
    fetchDataWithLoading(getAllProjects, [
      page + 1,
      rowsPerPage,
      searchValue,
      filters,
      isPTOLetterFilter,
      sort,
      getSizingPassedFilter,
      batteryOperationalizedFilter,
    ]);
  }, [
    fetchDataWithLoading,
    getAllProjects,
    page,
    rowsPerPage,
    searchValue,
    filters,
    isPTOLetterFilter,
    sort,
    getSizingPassedFilter,
    batteryOperationalizedFilter,
  ]);

  useEffect(() => {
    getProjectsReq();
  }, [getProjectsReq]);

  // Reset to first page when batteryOperationalized filter changes
  const handleBatteryOperationalizedChange = useCallback((value: string | undefined) => {
    setBatteryOperationalizedFilter(value);
    handleChangePage(null, 0);
  }, [handleChangePage]);

  const handleCloseModal = () => setDeleteProjectId(0);
  const handleCloseRestoreModal = () => setRestoreProjectId(0);
  const handleCloseErrorSnackbar = () => setErrorMessage(null);

  const togglePTOLetterFilter = () => setPTOLetterFilter((prev) => !prev);

  const handleArchiveProject = async () => {
    try {
      await archiveProjectReq(deleteProjectId);
      setDeleteProjectId(0);
      getProjectsReq();
    } catch (e) {
      console.error(e);
    }
  };

  const handleRestoreProject = async () => {
    try {
      await restoreProjectReq(restoreProjectId);
      setRestoreProjectId(0);
      getProjectsReq();
    } catch (e) {
      console.error(e);
    }
  };

  const updateEnrolmentStatus = async (
    projectId: ProjectRecord["id"],
    enrolmentStatus: string
  ) => {
    await updateEnrolmentStatusUtil({
      projectId,
      enrolmentStatus,
      isContractor: role === "contractor",
      onReject: setRejectProjectId,
      onCorrectionNeeded: setCorrectionNeededProjectId,
      onSuccess: getProjectsReq,
      onError: setErrorMessage,
    });
  };

  const navigateToProject = (rowId: number) => {
    const params = createSearchParams({
      ...(searchValue && { search: searchValue }),
      ...(filters.length && { filter: filters.join(",") }),
      ...(isPTOLetterFilter && { pto: `${isPTOLetterFilter}` }),
      ...(getSizingPassedFilter !== undefined && {
        sizing_passed: `${getSizingPassedFilter}`,
      }),
    });
    navigate({
      pathname: `/projects/${rowId}`,
      search: params?.toString(),
    });
  };

  const isAdmin = role === "administrator";
  const isOem = role === "oem";
  const isContractor = role === "contractor";

  useEffect(() => {
    setYScroll(window.innerHeight - 400);
  }, []);

  const showCreateProjectButton = isApplicationsActive && !isOem;

  return (
    <StyledTableCard sx={{ width: "100%" }}>
      {!isApplicationsActive && isApplicationsActive != null && (
        <Typography sx={{ padding: "12px", textAlign: "center" }}>
          We are currently not accepting new applications. Please follow our
          news for updates.
        </Typography>
      )}
      <CommonTableActions
        title={"Projects"}
        onSearch={handleDebounceSearchValue}
        sort={
          <>
            <TableSort
              placeholder={"Last updated"}
              items={LAST_UPDATED_SORT_ITEMS}
              setSortValue={handleSortChange}
            />
            {!downSm && isAdmin && (
              <>
                <PtoLetterCheckbox
                  isPTOLetterFilter={isPTOLetterFilter}
                  togglePTOLetterFilter={togglePTOLetterFilter}
                />
                <BatteryOperationalizedSelect
                  selectedValue={batteryOperationalizedFilter}
                  setSelectedValue={handleBatteryOperationalizedChange}
                />
              </>
            )}
          </>
        }
        filter={
          <>
            <TableFilter
              placeholder={"Incentive"}
              items={INCENTIVE_STATUS_ITEMS}
              setFilterValues={handleFilterChangeIncentive}
              filterValues={incentiveFilters}
            />
            <TableFilter
              placeholder={"Enrollment"}
              items={PROJECTS_FILTER_ITEMS}
              setFilterValues={handleFilterChangeEnrollment}
              filterValues={enrollmentFilters}
            />
            <TableFilter
              placeholder={"Sizing"}
              items={SIZING_FILTER_ITEMS}
              setFilterValues={handleFilterChangeSizing}
              filterValues={sizingFilters}
              singleSelection={true}
            />
          </>
        }
        rightActions={
          <>
            {showCreateProjectButton ? (
              <AddProjectModal getProjectsReq={getProjectsReq} />
            ) : null}
            {isAdmin && selectedRows && (
              <SendPerformanceIncentiveEmailButton
                projectIds={Array.from(selectedRows)}
              />
            )}
          </>
        }
      />
      {downSm && isAdmin && (
        <>
          <PtoLetterCheckbox
            isPTOLetterFilter={isPTOLetterFilter}
            togglePTOLetterFilter={togglePTOLetterFilter}
          />
          <Box sx={{ padding: "6px 12px" }}>
            <BatteryOperationalizedSelect
              selectedValue={batteryOperationalizedFilter}
              setSelectedValue={handleBatteryOperationalizedChange}
            />
          </Box>
        </>
      )}
      <ProjectsSelectedInfo
        pto={isPTOLetterFilter}
        search={searchValue}
        filter={filters}
        tableId={PROJECTS_TABLE_ID}
        batteryOperationalized={batteryOperationalizedFilter}
      />
      {downSm && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "10px 24px 10px 10px",
          }}
        >
          <GeneralCheckbox tableId={PROJECTS_TABLE_ID} /> Select all{" "}
        </Box>
      )}
      <TableContainer sx={{ maxHeight: yScroll }}>
        <StyledTable stickyHeader aria-label="customized table">
          <StyledTableHeader>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <GeneralCheckbox tableId={PROJECTS_TABLE_ID} />
              </StyledTableCell>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Customer name</StyledTableCell>
              <StyledTableCell>Address</StyledTableCell>
              <StyledTableCell>Incentive status</StyledTableCell>
              <StyledTableCell>Enrollment status</StyledTableCell>
              <StyledTableCell>Contractor</StyledTableCell>
              <StyledTableCell>Battery OEM</StyledTableCell>
              <StyledTableCell>Battery Model</StyledTableCell>
              <StyledTableCell>Battery Capacity (kWh)</StyledTableCell>
              {!isContractor && (
                <StyledTableCell>
                  Usable Capacity Incentive (kWh)
                </StyledTableCell>
              )}
              <StyledTableCell>Upfront Incentive Payment</StyledTableCell>
              <StyledTableCell>Notes</StyledTableCell>
              <StyledTableCell>Approval Date</StyledTableCell>
              {isAdmin && <StyledTableCell>Last Updated</StyledTableCell>}
              <StyledTableCell sx={{ minWidth: "150px" }}></StyledTableCell>
            </TableRow>
          </StyledTableHeader>
          <TableBody>
            {rows.map((row) =>
              downSm ? (
                <CollapsableRow
                  showCheckbox
                  rowId={row.id}
                  key={row.id}
                  title={row.customerName}
                  navigateTo={() => navigateToProject(row.id)}
                  firstSub={row?.id?.toString()?.padStart(3, "0")}
                  tableId={PROJECTS_TABLE_ID}
                  currentRowIds={currentRowIds}
                  secondSub={
                    <>
                      <IncentiveStatusSelect
                        tooltipContent={""}
                        truncateBadge={downSm}
                        projectId={row?.id}
                        incentiveStatus={row?.incentiveStatus}
                        getProjectsReq={getProjectsReq}
                        usableCapacity={row.usableCapacity}
                        numberOfBatteries={row.numberOfBatteries}
                      />
                      {!isContractor && (
                        <BadgeSelect
                          truncateBadge={downSm}
                          readOnly={role !== "administrator"}
                          currentSelectedItem={row.enrolmentStatus as string}
                          items={PROJECTS_FILTER_ITEMS}
                          onSelectItem={(value) =>
                            updateEnrolmentStatus(row.id, value)
                          }
                          badgeVariantFn={getEnrollmentBadgeVariant}
                        />
                      )}
                      {isContractor && (
                        <BadgeSelect
                          truncateBadge={downSm}
                          readOnly={
                            !getEnrollmentStatusChangeAllowed(
                              row.enrolmentStatus
                            )
                          }
                          currentSelectedItem={row.enrolmentStatus as string}
                          items={getEnrollmentStatusItems(row.enrolmentStatus)}
                          onSelectItem={(value) =>
                            updateEnrolmentStatus(row.id, value)
                          }
                          badgeVariantFn={getEnrollmentBadgeVariant}
                        />
                      )}
                    </>
                  }
                  secondSubSx={{ maxWidth: "210px" }}
                >
                  <StyledTooltip
                    title={row?.address?.length < 55 ? null : row.address}
                    enterDelay={500}
                  >
                    <StyledTableCellTruncated>
                      {row.address}
                    </StyledTableCellTruncated>
                  </StyledTooltip>
                  <CellWithHeader header={"Contractor"}>
                    {row.contractor}
                  </CellWithHeader>
                  <CellWithHeader header={"Battery model"}>
                    {row.batteryModel}
                  </CellWithHeader>
                  <CellWithHeader header={"Battery Capacity (kWh)"}>
                    {calculateUsableBatteryCapacity(
                      row.usableCapacity,
                      row.numberOfBatteries
                    )}
                  </CellWithHeader>
                  {!isContractor && (
                    <CellWithHeader header={"Usable Capacity Incentive (kWh)"}>
                      {row.usableCapacityIncentive
                        ? `${removeTrailZero(row.usableCapacityIncentive)} kWh`
                        : "Not set"}
                    </CellWithHeader>
                  )}
                  <CellWithHeader header={"Upfront Incentive Payment"}>
                    {formatCurrency(Number(row.upfrontIncentivePayment) / 100)}
                  </CellWithHeader>
                  <CellWithHeader header={"Notes"}>
                    {row.comment?.note}
                  </CellWithHeader>
                  <CellWithHeader header={"Approval Date"}>
                    {row.approvalDate}
                  </CellWithHeader>
                  <ProjectRowActions
                    getProjectsReq={getProjectsReq}
                    row={row as ProjectRecord}
                    setDeleteProjectId={setDeleteProjectId}
                    setRestoreProjectId={setRestoreProjectId}
                  />
                </CollapsableRow>
              ) : (
                <StyledTableRow
                  onClick={() => {
                    navigateToProject(row.id);
                  }}
                  sx={{ cursor: "pointer" }}
                  key={row.id}
                >
                  <StyledTableCell padding="checkbox">
                    <RowCheckbox
                      tableId={PROJECTS_TABLE_ID}
                      rowId={row.id}
                      currentRowIds={currentRowIds}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {row?.id?.toString()?.padStart(3, "0")}
                  </StyledTableCell>
                  <StyledTableCell sx={{ cursor: "pointer" }}>
                    {row.customerName}
                  </StyledTableCell>
                  <StyledTooltip
                    title={row?.address?.length < 55 ? null : row.address}
                    enterDelay={500}
                  >
                    <StyledTableCellTruncated>
                      {row.address}
                    </StyledTableCellTruncated>
                  </StyledTooltip>
                  <StyledTableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <IncentiveStatusSelect
                      tooltipContent={getStatusTooltipContent(
                        row?.latestIncentiveUpdate,
                        role
                      )}
                      projectId={row?.id}
                      incentiveStatus={row?.incentiveStatus}
                      getProjectsReq={getProjectsReq}
                      usableCapacity={row.usableCapacity}
                      numberOfBatteries={row.numberOfBatteries}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {!isContractor && (
                      <BadgeSelect
                        tooltipContent={getStatusTooltipContent(
                          row?.latestEnrolmentUpdate,
                          role
                        )}
                        readOnly={role !== "administrator"}
                        currentSelectedItem={row.enrolmentStatus as string}
                        items={PROJECTS_FILTER_ITEMS}
                        onSelectItem={(value) =>
                          updateEnrolmentStatus(row.id, value)
                        }
                        badgeVariantFn={getEnrollmentBadgeVariant}
                      />
                    )}
                    {isContractor && (
                      <BadgeSelect
                        tooltipContent={getStatusTooltipContent(
                          row?.latestEnrolmentUpdate,
                          role
                        )}
                        readOnly={
                          !getEnrollmentStatusChangeAllowed(row.enrolmentStatus)
                        }
                        currentSelectedItem={row.enrolmentStatus as string}
                        items={getEnrollmentStatusItems(row.enrolmentStatus)}
                        onSelectItem={(value) =>
                          updateEnrolmentStatus(row.id, value)
                        }
                        badgeVariantFn={getEnrollmentBadgeVariant}
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>{row.contractor}</StyledTableCell>
                  <StyledTableCell>{row.batteryOem}</StyledTableCell>
                  <StyledTableCell>{row.batteryModel}</StyledTableCell>
                  <StyledTableCell>
                    {calculateUsableBatteryCapacity(
                      row.usableCapacity,
                      row.numberOfBatteries
                    )}
                  </StyledTableCell>
                  {!isContractor && (
                    <StyledTableCell>
                      {row.usableCapacityIncentive
                        ? `${removeTrailZero(row.usableCapacityIncentive)} kWh`
                        : "Not set"}
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    {formatCurrency(Number(row.upfrontIncentivePayment) / 100)}
                  </StyledTableCell>
                  <StyledTooltip
                    title={
                      Number(row?.comment?.note?.length) < 55
                        ? null
                        : row.comment?.note
                    }
                    enterDelay={500}
                  >
                    <StyledTableCellTruncated>
                      {row.comment?.note}
                    </StyledTableCellTruncated>
                  </StyledTooltip>
                  <StyledTableCellTruncated>
                    {row.approvalDate}
                  </StyledTableCellTruncated>
                  <StyledPinnedCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{ 
                      minWidth: "171px", 
                      minHeight: "92px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ProjectRowActions
                      getProjectsReq={getProjectsReq}
                      row={row as ProjectRecord}
                      setDeleteProjectId={setDeleteProjectId}
                      setRestoreProjectId={setRestoreProjectId}
                    />
                  </StyledPinnedCell>
                </StyledTableRow>
              )
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TablePagination
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        totalCount={totalProjectsCount}
      />
      <MuiModal open={!!deleteProjectId} onClose={handleCloseModal}>
        <>
          <DangerModal
            onClick={handleArchiveProject}
            onClose={handleCloseModal}
            title={`Archive Project?`}
            subtitle={`You can restore it anytime.`}
            buttonText={"Archive"}
          />
        </>
      </MuiModal>
      <MuiModal open={!!restoreProjectId} onClose={handleCloseRestoreModal}>
        <>
          <DangerModal
            onClick={handleRestoreProject}
            onClose={handleCloseRestoreModal}
            title={`Restore Project?`}
            subtitle={`This action will restore the project to the active list.`}
            buttonText={"Restore"}
            variant={"yellow"}
          />
        </>
      </MuiModal>
      <RejectedProjectModal
        projectId={rejectProjectId}
        onClose={() => setRejectProjectId(0)}
        getProjectsReq={getProjectsReq}
      />
      <CorrectionNeededModal
        projectId={correctionNeededProjectId}
        onClose={() => setCorrectionNeededProjectId(0)}
        getProjectsReq={getProjectsReq}
      />
      {errorMessage && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{ zIndex: 1000 }}
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={handleCloseErrorSnackbar}
        >
          <Alert onClose={handleCloseErrorSnackbar} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </StyledTableCard>
  );
};

export default ProjectsTable;
